import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Bolt as BoltIcon,
  Shield as ShieldIcon,
  Link as LinkIcon,
  Analytics as AnalyticsIcon,
  Groups as GroupsIcon,
  Checklist as ChecklistIcon
} from '@mui/icons-material';

const BenefitDetail: React.FC = () => {
  const { benefitId } = useParams<{ benefitId: string }>();
  const navigate = useNavigate();

  // Benefit details data
  const benefitDetails: { [key: string]: any } = {
    'real-time-data-access': {
      icon: <BoltIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
      title: 'Real-Time Data Access',
      subtitle: 'Work with trusted, up-to-date information across all systems and domains',
      description: 'E-Unify provides instant access to your enterprise data with real-time synchronization across all connected systems. Our platform ensures that you always work with the most current, accurate information available.',
      keyFeatures: [
        'Live data synchronization across all connected systems',
        'Sub-second query response times for critical operations',
        'Automatic conflict resolution and data reconciliation',
        'Real-time data quality monitoring and alerts',
        'Cross-domain data access with unified security controls',
        'Streaming data integration for continuous updates'
      ],
      useCases: [
        {
          title: 'Immigration Case Processing',
          description: 'USCIS officers access real-time applicant data from multiple systems to make informed decisions without delays.'
        },
        {
          title: 'Border Security Operations',
          description: 'CBP agents receive instant updates on traveler risk assessments and watchlist changes.'
        },
        {
          title: 'Emergency Response',
          description: 'FEMA coordinators access live disaster data to allocate resources effectively during crises.'
        }
      ],
      technicalSpecs: [
        'Event-driven architecture with Apache Kafka',
        'In-memory caching with Redis for sub-millisecond access',
        'Change Data Capture (CDC) for real-time synchronization',
        'GraphQL and REST APIs for flexible data access',
        'WebSocket support for live data streaming'
      ]
    },
    'built-in-governance': {
      icon: <ShieldIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
      title: 'Built-In Governance',
      subtitle: 'Minimize regulatory risks with automated compliance controls and audit trails',
      description: 'Comprehensive data governance framework built into every layer of the platform. E-Unify automates compliance monitoring, policy enforcement, and audit trail generation to reduce risk and administrative burden.',
      keyFeatures: [
        'Automated policy enforcement across all data operations',
        'Complete audit trail with tamper-proof logging',
        'Role-based access control (RBAC) with fine-grained permissions',
        'Data classification and sensitivity labeling',
        'Automated compliance reporting for FISMA, FedRAMP, and NIST',
        'Data lineage tracking for regulatory requirements'
      ],
      useCases: [
        {
          title: 'FISMA Compliance Automation',
          description: 'Automatically generate FISMA compliance reports with complete audit trails and control evidence.'
        },
        {
          title: 'Privacy Act Enforcement',
          description: 'Enforce Privacy Act requirements with automated PII detection and access controls.'
        },
        {
          title: 'ATO Documentation',
          description: 'Generate Authority to Operate (ATO) documentation packages with automated evidence collection.'
        }
      ],
      technicalSpecs: [
        'Policy-as-code framework for automated enforcement',
        'Blockchain-based audit logging for immutability',
        'Integration with NIST 800-53 control frameworks',
        'Automated data classification using ML models',
        'Continuous compliance monitoring and alerting'
      ]
    },
    'seamless-integration': {
      icon: <LinkIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
      title: 'Seamless Integration',
      subtitle: 'Connect any system in minutes with our robust API ecosystem',
      description: 'E-Unify connects to virtually any data source through our comprehensive integration framework. Pre-built connectors for common government systems and a flexible API framework enable rapid integration without custom development.',
      keyFeatures: [
        'Pre-built connectors for 50+ government systems',
        'Universal API framework supporting REST, GraphQL, and SOAP',
        'No-code integration builder for business users',
        'Automated schema mapping and data transformation',
        'Legacy system integration with mainframe support',
        'Cloud and on-premises deployment flexibility'
      ],
      useCases: [
        {
          title: 'Multi-Agency Data Sharing',
          description: 'Connect DHS component systems (USCIS, ICE, CBP) for unified case management and intelligence sharing.'
        },
        {
          title: 'Legacy Modernization',
          description: 'Integrate mainframe systems with modern cloud applications without replacing existing infrastructure.'
        },
        {
          title: 'Partner Network Integration',
          description: 'Securely share data with state and local partners through standardized APIs and data exchange protocols.'
        }
      ],
      technicalSpecs: [
        'RESTful and GraphQL API endpoints',
        'JDBC/ODBC connectors for database integration',
        'Message queue integration (Kafka, RabbitMQ, IBM MQ)',
        'File-based integration (SFTP, S3, Azure Blob)',
        'Real-time and batch processing modes'
      ]
    },
    'self-service-analytics': {
      icon: <AnalyticsIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
      title: 'Self-Service Analytics',
      subtitle: 'Reduce time-to-insight with intuitive data exploration tools',
      description: 'Empower your team with self-service analytics capabilities that don\'t require technical expertise. E-Unify provides intuitive tools for data exploration, visualization, and reporting while maintaining governance and security.',
      keyFeatures: [
        'Drag-and-drop report builder with no coding required',
        'Interactive dashboards with real-time data updates',
        'Natural language query interface for business users',
        'Pre-built analytics templates for common use cases',
        'Advanced analytics with AI/ML model integration',
        'Automated insight generation and anomaly detection'
      ],
      useCases: [
        {
          title: 'Operational Dashboards',
          description: 'Create custom dashboards to monitor key performance indicators and operational metrics in real-time.'
        },
        {
          title: 'Ad-Hoc Analysis',
          description: 'Business analysts explore data and generate reports without waiting for IT support.'
        },
        {
          title: 'Predictive Analytics',
          description: 'Use built-in ML models to forecast trends and identify patterns in immigration, security, or operational data.'
        }
      ],
      technicalSpecs: [
        'Integration with Tableau, Power BI, and Qlik',
        'Built-in visualization library with 50+ chart types',
        'Natural language processing for query generation',
        'Python and R integration for advanced analytics',
        'Automated report scheduling and distribution'
      ]
    },
    'cross-agency-collaboration': {
      icon: <GroupsIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
      title: 'Cross-Agency Collaboration',
      subtitle: 'Break down silos with shared data products and workflows',
      description: 'Enable seamless collaboration across agencies and departments with secure data sharing, unified workflows, and collaborative analytics. E-Unify breaks down data silos while maintaining security and compliance.',
      keyFeatures: [
        'Secure multi-agency data sharing with granular permissions',
        'Collaborative workspaces for cross-functional teams',
        'Unified data products accessible across agencies',
        'Workflow automation for inter-agency processes',
        'Real-time collaboration on analytics and reports',
        'Federated identity management for seamless access'
      ],
      useCases: [
        {
          title: 'Joint Task Forces',
          description: 'Enable DHS, DOJ, and DOD teams to collaborate on national security investigations with shared data access.'
        },
        {
          title: 'Federal-State Partnerships',
          description: 'Share immigration and law enforcement data with state and local partners while maintaining federal security standards.'
        },
        {
          title: 'Inter-Agency Intelligence',
          description: 'Combine data from multiple intelligence sources for comprehensive threat assessment and analysis.'
        }
      ],
      technicalSpecs: [
        'OAuth 2.0 and SAML for federated authentication',
        'Attribute-based access control (ABAC) for fine-grained sharing',
        'Data masking and redaction for sensitive information',
        'Secure file sharing with encryption at rest and in transit',
        'Audit logging of all cross-agency data access'
      ]
    },
    'standardized-management': {
      icon: <ChecklistIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
      title: 'Standardized Management',
      subtitle: 'Enterprise-grade controls for consistent data practices',
      description: 'Establish and enforce enterprise-wide data standards with centralized management tools. E-Unify provides the governance framework and controls needed to ensure consistent, high-quality data practices across your organization.',
      keyFeatures: [
        'Centralized data catalog with enterprise-wide visibility',
        'Standardized data models and metadata schemas',
        'Automated data quality rules and validation',
        'Master data management (MDM) capabilities',
        'Data stewardship workflows and accountability',
        'Enterprise-wide policy management and enforcement'
      ],
      useCases: [
        {
          title: 'Data Standardization',
          description: 'Establish and enforce consistent data definitions, formats, and quality standards across all DHS components.'
        },
        {
          title: 'Master Data Management',
          description: 'Create golden records for people, organizations, and locations used across multiple systems and agencies.'
        },
        {
          title: 'Quality Assurance',
          description: 'Implement automated data quality checks and remediation workflows to maintain high data standards.'
        }
      ],
      technicalSpecs: [
        'Centralized metadata repository with versioning',
        'Data quality scoring and profiling engine',
        'Master data management with golden record creation',
        'Workflow engine for data stewardship processes',
        'Integration with enterprise data modeling tools'
      ]
    }
  };

  const benefit = benefitDetails[benefitId || ''];

  if (!benefit) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" color="error">Benefit not found</Typography>
        <Button onClick={() => navigate('/benefits')} sx={{ mt: 2 }}>
          Back to Benefits
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Hero Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6, px: 4, borderRadius: 2, mb: 4 }}>
        <Box>
          <Breadcrumbs 
            sx={{ mb: 2, color: 'rgba(255,255,255,0.7)' }}
            aria-label="breadcrumb"
          >
            <Link 
              color="inherit" 
              onClick={() => navigate('/benefits')}
              sx={{ cursor: 'pointer', '&:hover': { color: 'white' } }}
            >
              Benefits
            </Link>
            <Typography sx={{ color: 'white' }}>{benefit.title}</Typography>
          </Breadcrumbs>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
            {benefit.icon}
            <Typography variant="h2" sx={{ fontWeight: 700, color: 'white' }}>
              {benefit.title}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            {benefit.subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ mt: 2 }}>
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 3, p: 4 }}>
          <Container maxWidth="md">
            {/* Overview */}
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
              Overview
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
              {benefit.description}
            </Typography>

            {/* Key Features */}
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 4, mb: 2 }}>
              Key Features
            </Typography>
            <List>
              {benefit.keyFeatures.map((feature: string, index: number) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>

            {/* Use Cases */}
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 4, mb: 3 }}>
              Real-World Use Cases
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {benefit.useCases.map((useCase: any, index: number) => (
                <Grid item xs={12} key={index}>
                  <Card sx={{ p: 3, borderLeft: '4px solid #B31B1B' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                      {useCase.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {useCase.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Technical Specifications */}
            <Paper sx={{ p: 4, bgcolor: '#f5f5f5', mt: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                Technical Specifications
              </Typography>
              <List>
                {benefit.technicalSpecs.map((spec: string, index: number) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText primary={spec} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Container>
        </Paper>
      </Box>

      {/* CTA Section */}
      <Paper sx={{ bgcolor: 'primary.main', color: 'white', py: 6, px: 4, borderRadius: 2, mt: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
            Ready to Learn More?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', color: 'rgba(255,255,255,0.9)' }}>
            Contact us to see how {benefit.title} can transform your agency's data operations
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/benefits')}
              sx={{
                borderColor: 'white',
                borderWidth: 2,
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'white',
                  borderWidth: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.15)'
                }
              }}
            >
              Back to Benefits
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: '#f8f9fa'
                }
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default BenefitDetail;
